import { useEffect, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { getActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { s } from '@/theme/tokens';
import { Stack, Text } from '@/ui';
import { Box, Button, HelpOutline, IconButton, Tooltip } from '@/ui/primitives';
import { AddIcon } from '@/ui/primitives/icons';
import {
  defaultTagColor,
  TableRowTagEditorRow,
  type TableRowTagColorMenuState,
} from './TableRowTagEditorRow';
import type { TableActionsCellProps } from './TableCells.types';
import type { TagColor } from '@/shared/data/tags';

const rowNameCodeSx = {
  display: 'inline',
  backgroundColor: 'action.hover',
  borderRadius: s('xxs'),
  pl: s('sm'),
  pr: s('xs'),
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  boxDecorationBreak: 'clone',
} as const;

type TableRowTagsDialogProps = {
  editTagsEntityNoun: NonNullable<TableActionsCellProps['editTagsEntityNoun']>;
  loading: boolean;
  onClose: () => void;
  onConfirm?: (input: { tags: string[]; colors: TagColor[] }) => void;
  open: boolean;
  rowName: string;
  rowTagColors: TagColor[];
  rowTags: string[];
};

const normalizeTagsAndColors = (
  tags: string[],
  colors: TagColor[],
): { tags: string[]; colors: TagColor[] } => {
  const normalizedTags = tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0);
  const normalizedColors =
    normalizedTags.length === colors.length
      ? colors
      : normalizedTags.map((_tag, index) => colors[index] ?? defaultTagColor);
  return { tags: normalizedTags, colors: normalizedColors };
};

const getTagCascadeCopy = (
  editTagsEntityNoun: NonNullable<TableActionsCellProps['editTagsEntityNoun']>,
): string => {
  if (editTagsEntityNoun === 'suite') {
    return 'Tags will be applied to all associated suite runs, tests and test runs.';
  }
  if (editTagsEntityNoun === 'code directory') return 'Tags will be applied to this directory.';
  if (editTagsEntityNoun === 'code file') return 'Tags will be applied to this file.';
  if (editTagsEntityNoun === 'coverage run') return 'Tags will be applied to this coverage run.';
  if (editTagsEntityNoun === 'feature') return 'Tags will be applied only to this feature.';
  if (editTagsEntityNoun === 'release') return 'Tags will be applied only to this release.';
  if (editTagsEntityNoun === 'upload') {
    return 'Tags will be applied to all associated results files, execution runs, suite runs and test runs.';
  }
  if (editTagsEntityNoun === 'results file') {
    return 'Tags will be applied to all associated execution runs, suite runs and test runs.';
  }
  if (editTagsEntityNoun === 'execution run') {
    return 'Tags will be applied to all associated suite runs and test runs.';
  }
  if (editTagsEntityNoun === 'suite run') {
    return 'Tags will be applied to all associated test runs.';
  }
  if (editTagsEntityNoun === 'test run') return 'Tags will be applied only to this test run.';
  return 'Tags will be applied to all associated test runs.';
};

export const TableRowTagsDialog = ({
  editTagsEntityNoun,
  loading,
  onClose,
  onConfirm,
  open,
  rowName,
  rowTagColors,
  rowTags,
}: TableRowTagsDialogProps): JSX.Element => {
  const [colorMenuState, setColorMenuState] = useState<TableRowTagColorMenuState>(null);
  const [draftTags, setDraftTags] = useState<string[]>(rowTags.length > 0 ? rowTags : []);
  const [draftTagColors, setDraftTagColors] = useState<TagColor[]>(
    rowTags.length > 0 ? rowTags.map((_, index) => rowTagColors[index] ?? defaultTagColor) : [],
  );

  useEffect(() => {
    if (!open) return;
    setDraftTags(rowTags.length > 0 ? rowTags : []);
    setDraftTagColors(
      rowTags.length > 0 ? rowTags.map((_, index) => rowTagColors[index] ?? defaultTagColor) : [],
    );
  }, [open, rowTagColors, rowTags]);

  const handleOpenColorMenu = (index: number, anchorEl: HTMLElement): void => {
    setColorMenuState({ index, anchorEl });
  };

  const handleCloseColorMenu = (): void => {
    setColorMenuState(null);
  };
  const handleTagChange = (index: number, value: string): void => {
    setDraftTags((prev) => prev.map((item, itemIndex) => (itemIndex === index ? value : item)));
  };
  const handleTagColorChange = (index: number, color: TagColor): void => {
    setDraftTagColors((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? color : item)),
    );
  };
  const handleRemoveTag = (index: number): void => {
    setDraftTags((prev) => prev.filter((_item, itemIndex) => itemIndex !== index));
    setDraftTagColors((prev) => prev.filter((_item, itemIndex) => itemIndex !== index));
  };

  const normalizedInitialTagsAndColors = normalizeTagsAndColors(
    rowTags.length > 0 ? rowTags : [],
    rowTags.length > 0 ? rowTags.map((_, index) => rowTagColors[index] ?? defaultTagColor) : [],
  );
  const normalizedDraftTagsAndColors = normalizeTagsAndColors(draftTags, draftTagColors);
  const hasTagChanges =
    normalizedDraftTagsAndColors.tags.length !== normalizedInitialTagsAndColors.tags.length ||
    normalizedDraftTagsAndColors.colors.length !== normalizedInitialTagsAndColors.colors.length ||
    normalizedDraftTagsAndColors.tags.some(
      (tag, index) => tag !== normalizedInitialTagsAndColors.tags[index],
    ) ||
    normalizedDraftTagsAndColors.colors.some(
      (color, index) => color !== normalizedInitialTagsAndColors.colors[index],
    );
  const cascadeCopy = getTagCascadeCopy(editTagsEntityNoun);

  return (
    <ConfirmDialog
      open={open}
      title={getDialogMessage('entity.edit.title', { entity: 'tag', plural: true })}
      description={
        <Stack gap="md">
          <Text role="body" color="textSecondary">
            Update tags for {editTagsEntityNoun}{' '}
            <Box component="span" sx={rowNameCodeSx}>
              {rowName}
            </Box>
            .
          </Text>
        </Stack>
      }
      confirmLabel={getActionLabel('update')}
      confirmColor="primary"
      confirmDisabled={!hasTagChanges}
      footerGap="xs"
      loading={loading}
      onClose={onClose}
      onConfirm={() => {
        onConfirm?.(normalizedDraftTagsAndColors);
        onClose();
      }}
    >
      <Stack gap="none" sx={{ width: '100%', mt: s('none') }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: s('xs') }}>
          <Text
            role="label"
            color="textSecondary"
            sx={{ fontSize: (theme) => theme.typography.subtitle2.fontSize }}
          >
            Tags
          </Text>
          <Tooltip
            title={
              <Text
                role="body"
                sx={(theme) => ({
                  fontSize: theme.typography.subtitle1.fontSize,
                  lineHeight: theme.typography.subtitle1.lineHeight,
                })}
              >
                {cascadeCopy}
              </Text>
            }
            placement="top"
            slotProps={{
              tooltip: {
                sx: (theme) => ({
                  fontSize: theme.typography.subtitle1.fontSize,
                  lineHeight: theme.typography.subtitle1.lineHeight,
                  maxWidth: 440,
                }),
              },
            }}
          >
            <IconButton
              size="small"
              aria-label="More information about tags"
              sx={{ p: s('none'), color: 'text.secondary' }}
            >
              <HelpOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Stack gap="md" sx={{ mt: s('md') }}>
          {draftTags.map((tag, index) => (
            <TableRowTagEditorRow
              key={`${rowName}-tag-${index}`}
              colorMenuState={colorMenuState}
              index={index}
              onCloseColorMenu={handleCloseColorMenu}
              onColorChange={handleTagColorChange}
              onOpenColorMenu={handleOpenColorMenu}
              onRemove={handleRemoveTag}
              onTagChange={handleTagChange}
              selectedColor={draftTagColors[index] ?? defaultTagColor}
              tag={tag}
            />
          ))}
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: s('md') }}>
          <Button
            variant="text"
            startIcon={<AddIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
            onClick={() => {
              setDraftTags((prev) => [...prev, '']);
              setDraftTagColors((prev) => [...prev, defaultTagColor]);
            }}
            sx={{
              pl: s('none'),
              minWidth: 0,
              '& .MuiButton-startIcon': {
                marginLeft: s('none'),
                marginRight: s('xxxs'),
              },
            }}
          >
            {getActionLabel('addTag')}
          </Button>
        </Box>
      </Stack>
    </ConfirmDialog>
  );
};
